import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import useOffersUI from 'api/useOffersUI';
import usePermissions from 'api/usePermissions';
import useTheme from 'api/useTheme';
import { ETaskId, OfferUI } from 'common/contracts';
import { useUnsavedChanges } from 'hooks/useUnsavedChanges';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';
import { EToasterStatus, showToast } from 'utils/showToast';
import { ROUTE_SELECT_TYPE, SAVE_VALIDATION_MESSAGES, SUBTITLE_SUPPORTED_TEMPLATES } from 'views/OfferDesign/OfferDesign.consts';
import { OfferDesignFormValues, OfferDesignTypes } from 'views/OfferDesign/OfferDesign.types';
import { getFirstSaveError } from 'views/OfferDesign/OfferDesign.utils';
import { OfferFormLayout } from 'views/Offers/components/OfferFormLayout';

import Button from 'components/Button/Button';
import DialogModal from 'components/DialogModal/DialogModal';

import { OfferDesignPreview } from './components/OfferDesignPreview/OfferDesignPreview';
import { FormFieldsContext, useFormFields } from './context';
import { INITIAL_VALUES_BUILDERS, OFFER_TYPE_CONFIG } from './OfferDesignForm.consts';
import { buildInitialValues } from './utils';

export const OfferDesignForm = () => {
  const { publisherId, offerUIId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isDup = location.pathname.includes('/dup/');
  const isEdit = !!offerUIId && !isDup;
  const isCreate = !isEdit && !isDup;
  const searchParams = new URLSearchParams(location.search);
  const offerType = (searchParams.get('type') as OfferDesignTypes) || OfferDesignTypes.Bundle;
  const offerSubType = searchParams.get('subType') || '';
  const config = OFFER_TYPE_CONFIG[offerType];

  const { canUserEdit } = usePermissions();
  const { setTaskStatus, hasCategories, isWebStore } = useChecklistState();
  const { addOfferUI, updateOfferUI, getOfferUI, getOffersUI } = useOffersUI(publisherId, offerUIId);
  const { getGeneralTheme: generalTheme } = useTheme(publisherId);

  const [isNameUnique, setIsNameUnique] = useState(true);
  const [isExternalIdUnique, setIsExternalIdUnique] = useState(true);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const pendingNavigationRef = useRef<() => void>(() => { });

  const shouldDisplaySubTitle = useMemo(
    () =>
      !!generalTheme?.data?.specialOffersInternalViewModel &&
      SUBTITLE_SUPPORTED_TEMPLATES.includes(generalTheme.data.specialOffersInternalViewModel),
    [generalTheme.data?.specialOffersInternalViewModel]
  );

  const initialValues = useMemo(
    () => buildInitialValues(offerType, getOfferUI?.data, isDup, offerSubType, INITIAL_VALUES_BUILDERS),
    [getOfferUI?.data, isDup, offerType, offerSubType]
  );

  const formTitle = useMemo(() => {
    if (isEdit) return 'Edit offer design';
    if (isDup) return 'Duplicate offer design';
    return 'Create new offer design';
  }, [isEdit, isDup]);

  const navigateToTable = useCallback(() => navigate('../'), [navigate]);

  const navigateBack = useCallback(
    () => navigate(isCreate ? `../${ROUTE_SELECT_TYPE}` : '../'),
    [navigate, isCreate]
  );

  const handleDiscardConfirm = useCallback(() => {
    setIsDiscardModalOpen(false);
    pendingNavigationRef.current();
  }, []);

  const handleSubmitSuccess = useCallback(() => {
    showToast({
      message: `Offer Design ${isEdit ? 'saved' : 'added'} successfully`,
      status: EToasterStatus.SUCCESS,
    });
    if (!isEdit && hasCategories && isWebStore) {
      setTaskStatus(ETaskId.CREATE_OFFER_DESIGN);
    }
    (isEdit ? getOfferUI : getOffersUI).refetch();
    navigateToTable();
  }, [isEdit, hasCategories, isWebStore, setTaskStatus, getOfferUI, getOffersUI, navigateToTable]);

  const handleSubmitError = useCallback(() => {
    showToast({
      message: `Error ${isEdit ? 'editing' : 'adding'} Offer Design`,
      status: EToasterStatus.ERROR,
    });
  }, [isEdit]);

  const handleSubmit = useCallback(
    async (formValues: OfferDesignFormValues): Promise<void> => {
      if (!config) return;
      const payload = config.buildPayload(formValues, offerType);
      const callbacks = { onSuccess: handleSubmitSuccess, onError: handleSubmitError };

      if (isEdit && offerUIId) {
        updateOfferUI.mutate({ offerId: offerUIId, form: payload }, callbacks);
      } else {
        addOfferUI.mutate(payload, callbacks);
      }
    },
    [config, offerType, handleSubmitSuccess, handleSubmitError, isEdit, offerUIId, updateOfferUI, addOfferUI]
  );

  const uniqueFlags = useMemo(
    () => ({ isNameUnique, isExternalIdUnique }),
    [isNameUnique, isExternalIdUnique]
  );

  const { contextValue, submitForm, isValid, dirty, values, validateForm, setTouched } = useFormFields({
    initialValues,
    validationSchema: config?.schema,
    onSubmit: handleSubmit,
    uniqueFlags
  });

  const handleSave = useCallback(async () => {
    const validationErrors = await validateForm();

    const allTouched = Object.keys(initialValues).reduce<Record<string, boolean>>(
      (acc, key) => ({ ...acc, [key]: true }),
      {}
    );
    setTouched(allTouched, false);

    const firstError = getFirstSaveError(validationErrors, [
      { failed: !isNameUnique, ...SAVE_VALIDATION_MESSAGES.nameUnique },
      { failed: !isExternalIdUnique, ...SAVE_VALIDATION_MESSAGES.externalIdUnique },
      { failed: !canUserEdit, ...SAVE_VALIDATION_MESSAGES.permission },
    ], Object.keys(initialValues));

    if (firstError) {
      showToast({ ...firstError, status: EToasterStatus.ERROR });
      return;
    }

    if (isEdit && !dirty) {
      navigateToTable();
      return;
    }

    submitForm();
  }, [validateForm, initialValues, setTouched, isNameUnique, isExternalIdUnique, canUserEdit, isEdit, dirty, submitForm, navigateToTable]);

  const navigateWithDirtyCheck = useCallback(
    (navigateFn: () => void) => {
      if (dirty) {
        pendingNavigationRef.current = navigateFn;
        setIsDiscardModalOpen(true);
      } else {
        navigateFn();
      }
    },
    [dirty]
  );

  const handleClose = useCallback(
    () => navigateWithDirtyCheck(navigateToTable),
    [navigateWithDirtyCheck, navigateToTable]
  );

  const handleBack = useCallback(
    () => navigateWithDirtyCheck(navigateBack),
    [navigateWithDirtyCheck, navigateBack]
  );

  const isSaveEnabled = useMemo(
    () => !addOfferUI.isLoading && !updateOfferUI.isLoading,
    [addOfferUI.isLoading, updateOfferUI.isLoading]
  );

  useEffect(() => {
    if (offerUIId && publisherId) {
      getOfferUI.refetch();
    }
  }, [offerUIId, publisherId]);

  useEffect(() => {
    if (getOffersUI.data?.result) {
      const others = getOffersUI.data.result.filter(
        (o: OfferUI) => !isEdit || o._id !== offerUIId
      );
      setIsNameUnique(!others.some((o: OfferUI) => o.name === values.name));
      setIsExternalIdUnique(!others.some((o: OfferUI) => o.externalId === values.externalId));
    }
  }, [getOffersUI.data, isEdit, offerUIId, values.name, values.externalId]);

  useUnsavedChanges({ dirty });

  if (!config) return null;

  const { FormFields } = config;

  return (
    <React.Fragment>
      {isDiscardModalOpen && (
        <DialogModal
          isOpen={isDiscardModalOpen}
          title="Discard changes?"
          description="You have unsaved changes. If you go back now, your changes will not be saved."
          modalActions={{
            onConfirm: {
              label: 'Discard changes',
              action: handleDiscardConfirm,
              variant: 'default',
            },
            onClose: {
              label: 'Cancel',
              action: () => setIsDiscardModalOpen(false),
              variant: 'link',
            },
          }}
        />
      )}
      <OfferFormLayout
        title={formTitle}
        isFormValid={isSaveEnabled}
        onSave={handleSave}
        onClose={handleClose}
        previewContent={<OfferDesignPreview offerType={offerType} values={values} />}
        showPreviewToggle={false}
      >
        <div className="w-[600px] pt-10 pb-10 h-max">
          <Button
            variant="link"
            onClick={handleBack}
            className="p-0 h-auto font-semibold mb-5 gap-[9px] text-indigo-600 no-underline hover:no-underline hover:text-indigo-600"
          >
            <FontAwesomeIcon icon="fa-regular fa-arrow-left" />
            Back
          </Button>
          <FormFieldsContext.Provider value={contextValue}>
            <div className="rounded-lg border border-base-border p-9">
              <FormFields
                shouldDisplaySubTitle={shouldDisplaySubTitle}
                specialOffersInternalViewModel={generalTheme?.data?.specialOffersInternalViewModel}
              />
            </div>
          </FormFieldsContext.Provider>
        </div>
      </OfferFormLayout>
    </React.Fragment>
  );
};
